<template>
  <div ref="rootRef" data-component-name="AppCalendar">
    <VueDatePicker
      v-model="date"
      ref="datepicker"
      :action-row="{
        showPreview: false,
      }"
      :clearable="false"
      :enable-time-picker="false"
      :hide-navigation="['month', 'year']"
      :highlight="{
        dates: highlightedDates,
      }"
      :locale
      :start-time="[
        { hours: 0, minutes: 0, seconds: 0 },
        { hours: 23, minutes: 59, seconds: 59 },
      ]"
      model-auto
      partial-range
      range
      model-type="yyyy-MM-dd HH:mm:ss"
      offset="4"
      @open="openHandler"
      @closed="closeHandler"
      @update-month-year="updateVisibleRange"
    >
      <template #dp-input="{ value, isMenuOpen }">
        <div
          :class="[
            'trigger',
            {
              hasFocus: isMenuOpen,
              hasValue: value.length,
            },
          ]"
        >
          <label>{{ $t('Date') }}</label>
          <span class="nowrap">{{ value }}</span>

          <button>
            <SvgIcon name="chevron-down" />
          </button>
        </div>
      </template>

      <template #arrow-left>
        <SvgIcon name="chevron-left" />
      </template>

      <template #arrow-right>
        <SvgIcon name="chevron-right" />
      </template>

      <template #action-buttons>
        <Buttons.Regular
          class="clear"
          fill="none"
          accent="black"
          @click="clearHandler"
        >
          {{ $t('Clear') }}
        </Buttons.Regular>

        <Buttons.Regular
          class="cancel"
          fill="none"
          accent="black"
          @click="cancelHandler"
        >
          {{ $t('Cancel') }}
        </Buttons.Regular>

        <Buttons.Regular
          class="select"
          fill="none"
          accent="black"
          @click="selectHandler"
        >
          {{ $t('Select') }}
        </Buttons.Regular>
      </template>
    </VueDatePicker>
  </div>
</template>

<script setup lang="ts">
// https://vue3datepicker.com/
import VueDatePicker from '@vuepic/vue-datepicker';
import type { DatePickerInstance } from '@vuepic/vue-datepicker';
import '@vuepic/vue-datepicker/dist/main.css';

// composables
import { useI18n } from '#i18n';
import { useDayjs } from '#dayjs';

// components
import Buttons from '~/components/redesign/Buttons';

// utils
import pick from 'lodash.pick';
import getMonthRange from '~/utils/getMonthRange';
import emitNativeEvent from '~/utils/emitNativeEvent';

const props = withDefaults(
  defineProps<{
    from?: string;
    to?: string;
    highlightedDates?: Date[];
  }>(),
  {
    highlightedDates: () => [],
  },
);

const emit = defineEmits<{
  'update:from': [value: string];
  'update:to': [value: string];
}>();

const visibleRange =
  defineModel<ReturnType<typeof getMonthRange>>('visibleRange');

const dayjs = useDayjs();
const { locale } = useI18n();

const date = computed({
  get: () => {
    const propsValues = Object.values(pick(props, ['from', 'to']));

    return propsValues.every(Boolean) ? propsValues : propsValues[0];
  },
  set: (value: null | string | string[]) => {
    let from: typeof props.from;
    let to: typeof props.to;

    if (Array.isArray(value)) {
      [from, to] = value;
    } else if (value !== null) {
      from = value;
      to = dayjs(from)
        .set('hour', 23)
        .set('minute', 59)
        .set('second', 59)
        .format('YYYY-MM-DD HH:mm:ss');
    }

    emit('update:from', from);
    emit('update:to', to);
  },
});

const rootRef = ref<null | HTMLDivElement>(null);
const datepicker = ref<DatePickerInstance>(null);

const emitNativeClick = () => {
  if (!(rootRef.value instanceof HTMLDivElement)) return;
  emitNativeEvent(rootRef.value, 'click');
};

/**
 * Returns the visible date range for the calendar.
 * If the "to" prop is set, it uses its month and year; otherwise, the current month and year.
 *
 * @returns Object with month and year.
 */
const getVisibleRange = () => {
  const date = dayjs(props.to)
    .set('hour', 0)
    .set('minute', 0)
    .set('millisecond', 0);

  return {
    month: date.month(),
    year: date.year(),
  };
};

const openHandler = () => {
  emitNativeClick();
  updateVisibleRange(getVisibleRange());
};

const closeHandler = () => {
  emitNativeClick();
};

const clearHandler = () => {
  if (!datepicker.value) return;
  datepicker.value.clearValue();
};

const cancelHandler = () => {
  if (!datepicker.value) return;
  datepicker.value.closeMenu();
};

const selectHandler = () => {
  if (!datepicker.value) return;
  datepicker.value.selectDate();
};

const updateVisibleRange = ({
  month,
  year,
}: {
  month: number;
  year: number;
}) => {
  visibleRange.value = getMonthRange(month, year);
};
</script>

<style lang="scss">
@import '$/mixins/flex';
@import '$/mixins/media';
@import '$/mixins/size';
@import '$/mixins/typography';
@import '$/functions/token';
@import '$/variables/fonts';
@import '$/variables/shadows';

[data-component-name='AppCalendar'] {
  width: 100%;
}

.dp {
  &--header {
    &-wrap {
      padding: 0 0.5rem;
    }
  }

  &--menu {
    &-wrapper {
      z-index: 1;
      top: 0.5rem !important;
      left: unset !important;
      right: 0 !important;

      @include tablet {
        right: 50% !important;
        transform: translateX(50%);
      }
    }
  }

  &__inner {
    &_nav {
      border-radius: unset;

      &:active {
        background: rgba(token('accent-hover'), 24%);
      }
    }
  }

  &__menu {
    border: unset !important;
    box-shadow: map-get($shadows, 'Elevation 3');
  }

  &__calendar {
    &_header {
      font-weight: 400;

      &_item {
        @include flex-center-center;
      }

      &_separator {
        display: none;
      }
    }

    &_item {
      padding: 0.25rem;
      width: calc(100% / 7); // 7 days per week
      aspect-ratio: 1/1;
    }
  }

  &__range {
    &_start {
      position: relative;

      &:after {
        content: '';
        display: block;

        position: absolute;
        left: 100%;

        width: 0.25rem;
        height: 100%;

        background-color: rgba(token('accent-hover'), 16%);
      }
    }

    &_between {
      border: unset;
      position: relative;

      background-color: var(--dp-range-between-dates-background-color);

      &:before {
        content: '';
        display: block;

        position: absolute;
        right: 100%;

        width: 0.25rem;
        height: 100%;

        background-color: rgba(token('accent-hover'), 16%);
      }

      &:after {
        content: '';
        display: block;

        position: absolute;
        left: 100%;

        width: 0.25rem;
        height: 100%;

        background-color: rgba(token('accent-hover'), 16%);
      }
    }

    &_end {
      position: relative;

      color: var(--dp-primary-text-color) !important;

      &:before {
        content: '';
        display: block;

        position: absolute;
        right: 100%;

        width: 0.25rem;
        height: 100%;

        background-color: rgba(token('accent-hover'), 16%);
      }
    }
  }

  &__arrow {
    &_top {
      display: none;
    }
  }

  &__action {
    &_buttons {
      @include flex-center-sb;
      min-width: 100%;

      .clear {
        margin-right: auto;
      }

      .cancel {
      }

      .select {
        margin-left: 1rem;
      }
    }
  }

  &__active {
    &_date {
      background: var(--dp-primary-color) !important;
      color: var(--dp-primary-text-color) !important;
    }
  }

  &__btn {
    position: relative;

    &:active {
      background: rgba(token('accent-hover'), 24%);
    }
  }

  &__cell {
    &_offset {
      color: var(--dp-secondary-color);
    }

    &_inner {
      height: 100%;
      width: 100%;

      &:not(.dp__range_start):not(.dp__range_end):not(.dp__active_date) {
        &:hover {
          color: var(--dp-hover-text-color) !important;
        }
      }

      &:not(.dp__cell_offset):not(.dp__range_start):not(.dp__range_end):not(
          .dp__cell_highlight
        ) {
        color: token('text-primary');
      }

      &:not(.dp__today) {
        border: unset;
      }

      &:active {
        background: rgba(token('accent-hover'), 24%);
      }

      &.dp__cell_offset:active {
        color: var(--dp-hover-text-color);
      }
    }

    &_highlight {
      font-weight: 600;
    }
  }

  &__month {
    &_year {
      &_select {
        font-weight: 600;

        height: calc(100% - 1rem);
        margin-top: 0.5rem;

        &:after {
          content: '';
          display: block;
          box-sizing: border-box;

          border-top: 4px solid token('icon-primary');
          border-left: 4px solid transparent;
          border-right: 4px solid transparent;

          transition: transform 0.15s ease;

          margin-left: 0.75rem;
        }
      }
    }
  }

  &__overlay {
    &_row {
      @include flex-center-sb;
    }

    &_col {
      width: auto;
      padding: 0.25rem;
    }

    &_cell {
      &_pad {
        padding: 0.62rem;
      }
    }
  }

  &__input {
    &_wrap {
      .trigger {
        padding: 1rem;
        border: 1px solid token('outline-secondary');
        @include body-3;

        background-color: transparent;
        transition: background-color 0.15s ease;

        @include flex-center-sb;
        gap: 0 1rem;

        cursor: pointer;

        label {
          cursor: pointer;
          color: token('text-secondary');

          position: absolute;
          top: 50%;
          transform: translate(0, -50%);
        }

        span {
          color: token('text-primary');
        }

        button {
          @include fixed-size(1.5rem);

          svg {
            @include full-size;
          }
        }

        &:not(&.hasFocus):hover {
          background-color: rgba(token('accent-hover'), 16%);
        }

        &:not(&.hasFocus):active {
          background-color: rgba(token('accent-hover'), 24%);
        }

        &.hasFocus,
        &.hasValue {
          label {
            @include caption;
            padding: 0 0.25rem;
            background-color: token('surf-cont-primary');

            top: 0;
          }
        }

        &.hasFocus {
          border: 1px solid token('outline-action');
        }
      }
    }
  }
}

.dp__calendar_row {
  .dp__calendar_item {
    &:first-of-type {
      .dp__range_between {
        &:before {
          width: 0.75rem;
        }
      }

      .dp__range_end {
        &:before {
          width: 0.75rem;
        }
      }
    }

    &:last-of-type {
      .dp__range_start {
        &:after {
          width: 0.75rem;
        }
      }

      .dp__range_between {
        &:after {
          width: 0.75rem;
        }
      }
    }

    .dp__range_start.dp__range_end {
      &:before,
      &:after {
        display: none;
      }
    }
  }
}

.dp__theme_dark,
.dp__theme_light {
  --dp-background-color: #{token('surface')};
  --dp-text-color: #{token('text-primary')};
  --dp-hover-color: #{rgba(token('accent-hover'), 16%)};
  --dp-hover-text-color: #{token('text-primary')} !important;
  --dp-hover-icon-color: #959595;
  --dp-primary-color: #{token('surf-cont-prim-action')};
  --dp-primary-disabled-color: #6bacea;
  --dp-primary-text-color: #{token('text-reversed')};
  --dp-secondary-color: #{token('text-secondary')};
  --dp-menu-border-color: #ddd;
  --dp-border-color: #{token('outline-primary')};
  --dp-border-color-hover: #{token('outline-primary')};
  --dp-border-color-focus: #{token('outline-action')};
  --dp-disabled-color: #f6f6f6;
  --dp-scroll-bar-background: #f3f3f3;
  --dp-scroll-bar-color: #959595;
  --dp-success-color: #76d275;
  --dp-success-color-disabled: #a3d9b1;
  --dp-icon-color: #959595;
  --dp-danger-color: #ff6f60;
  --dp-marker-color: #ff6f60;
  --dp-tooltip-color: #fafafa;
  --dp-disabled-color-text: #8e8e8e;
  --dp-highlight-color: unset;
  --dp-range-between-dates-background-color: var(--dp-hover-color, #f3f3f3);
  --dp-range-between-dates-text-color: var(--dp-hover-text-color, #212121);
  --dp-range-between-border-color: var(--dp-hover-color, #f3f3f3);

  --dp-font-family: #{map-get($fonts, 'Inter')};
  --dp-border-radius: 0;
  --dp-cell-border-radius: 0;
  --dp-common-transition: all 0.1s ease-in;

  --dp-button-height: 35px;
  --dp-month-year-row-height: 35px;
  --dp-month-year-row-button-size: 3.5rem;
  --dp-button-icon-height: 1.5rem;
  --dp-cell-size: 2.5rem;
  --dp-cell-padding: 5px;
  --dp-common-padding: 0.62rem;
  --dp-input-icon-padding: 35px;
  --dp-input-padding: 1rem;
  --dp-menu-min-width: 260px;
  --dp-action-button-height: auto;
  --dp-action-buttons-padding: 1rem;
  --dp-row-margin: 0 0;
  --dp-calendar-header-cell-padding: 0.5rem;
  --dp-two-calendars-spacing: 10px;
  --dp-overlay-col-padding: 0;
  --dp-time-inc-dec-button-size: 32px;
  --dp-menu-padding: 0 0.5rem;
  --dp-action-row-padding: 0.5rem;

  --dp-font-size: 1rem;
  --dp-preview-font-size: 1.125rem;
  --dp-time-font-size: 0.8rem;

  --dp-animation-duration: 0.1s;
  --dp-menu-appear-transition-timing: cubic-bezier(0.4, 0, 1, 1);
  --dp-transition-timing: ease-out;
}
</style>
